/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { assert } from '@fluidframework/common-utils';
import { assertNotUndefined } from './Common';
import { Side } from './persisted-types';
import { getNodeId } from './NodeIdUtilities';
/**
 * The type of a Change
 * @public
 */
export var ChangeType;
(function (ChangeType) {
    ChangeType[ChangeType["Insert"] = 0] = "Insert";
    ChangeType[ChangeType["Detach"] = 1] = "Detach";
    ChangeType[ChangeType["Build"] = 2] = "Build";
    ChangeType[ChangeType["SetValue"] = 3] = "SetValue";
    ChangeType[ChangeType["Constraint"] = 4] = "Constraint";
})(ChangeType || (ChangeType = {}));
// Note: Documentation of this constant is merged with documentation of the `Change` interface.
/**
 * @public
 */
export const Change = {
    build: (source, destination) => ({
        destination,
        source,
        type: ChangeType.Build,
    }),
    insert: (source, destination) => ({
        destination,
        source,
        type: ChangeType.Insert,
    }),
    detach: (source, destination) => ({
        destination,
        source,
        type: ChangeType.Detach,
    }),
    setPayload: (nodeToModify, payload) => ({
        nodeToModify,
        payload,
        type: ChangeType.SetValue,
    }),
    clearPayload: (nodeToModify) => ({
        nodeToModify,
        // Rationale: 'undefined' is reserved for future use (see 'SetValue' interface above.)
        payload: null,
        type: ChangeType.SetValue,
    }),
    constraint: (toConstrain, effect, identityHash, length, contentHash, parentNode, label) => ({
        toConstrain,
        effect,
        identityHash,
        length,
        contentHash,
        parentNode,
        label,
        type: ChangeType.Constraint,
    }),
    /** Helpers for making high-level composite operations */
    /**
     * @returns a change that deletes the supplied part of the tree.
     */
    delete: (stableRange) => Change.detach(stableRange),
    /**
     * @returns changes that insert 'nodes' into the specified location in the tree.
     */
    insertTree: (nodes, destination) => {
        const build = Change.build(nodes, 0);
        return [build, Change.insert(build.destination, destination)];
    },
    /**
     * @returns changes that moves the specified content to a new location in the tree.
     */
    move: (source, destination) => {
        const detach = Change.detach(source, 0);
        return [detach, Change.insert(assertNotUndefined(detach.destination), destination)];
    },
};
/**
 * The remainder of this file consists of ergonomic factory methods for persisted types, or common combinations thereof (e.g. "Move" as a
 * combination of a "Detach" change and an "Insert" change).
 *
 * None of these helpers are persisted in documents, and therefore changes to their semantics need only follow standard semantic versioning
 * practices.
 */
// Note: Documentation of this constant is merged with documentation of the `StablePlace` interface.
/**
 * @public
 */
export const StablePlace = {
    /**
     * @returns The location directly before `node`.
     */
    before: (node) => ({
        side: Side.Before,
        referenceSibling: getNodeId(node),
    }),
    /**
     * @returns The location directly after `node`.
     */
    after: (node) => ({ side: Side.After, referenceSibling: getNodeId(node) }),
    /**
     * @returns The location at the start of `trait`.
     */
    atStartOf: (trait) => ({ side: Side.After, referenceTrait: trait }),
    /**
     * @returns The location at the end of `trait`.
     */
    atEndOf: (trait) => ({ side: Side.Before, referenceTrait: trait }),
};
// Note: Documentation of this constant is merged with documentation of the `StableRange` interface.
/**
 * @public
 */
export const StableRange = {
    /**
     * Factory for producing a `StableRange` from a start `StablePlace` to an end `StablePlace`.
     * @example
     * StableRange.from(StablePlace.before(startNode)).to(StablePlace.after(endNode))
     */
    from: (start) => ({
        to: (end) => {
            if (start.referenceTrait && end.referenceTrait) {
                assert(start.referenceTrait.parent === end.referenceTrait.parent, 0x5fe /* StableRange must be constructed with endpoints from the same trait */);
                assert(start.referenceTrait.label === end.referenceTrait.label, 0x5ff /* StableRange must be constructed with endpoints from the same trait */);
            }
            return { start, end };
        },
    }),
    /**
     * @returns a `StableRange` which contains only the provided `node`.
     * Both the start and end `StablePlace` objects used to anchor this `StableRange` are in terms of the passed in node.
     */
    only: (node) => ({
        start: StablePlace.before(node),
        end: StablePlace.after(node),
    }),
    /**
     * @returns a `StableRange` which contains everything in the trait.
     * This is anchored using the provided `trait`, and is independent of the actual contents of the trait:
     * it does not use sibling anchoring.
     */
    all: (trait) => ({
        start: StablePlace.atStartOf(trait),
        end: StablePlace.atEndOf(trait),
    }),
};
//# sourceMappingURL=ChangeTypes.js.map